// GA4 Analytics Service - Privacy-first, trauma-informed tracking
// This service implements consent-based analytics with strict PII protection

const GA4_MEASUREMENT_ID = 'G-GLYCZQF0GZ';

// Blocked keys that may contain sensitive content
const BLOCKED_KEYS = [
  'content', 'text', 'journal', 'prayer', 'testimony', 'email', 'name',
  'message', 'note', 'reflection', 'entry', 'personal', 'private'
];

// Sanitize event data to remove any potential PII or sensitive content
export function sanitizeEventData(data: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(data).filter(([key]) => 
      !BLOCKED_KEYS.some(blocked => key.toLowerCase().includes(blocked))
    ).map(([key, value]) => {
      // Also sanitize string values that might contain sensitive data
      if (typeof value === 'string' && value.length > 100) {
        return [key, '[content_redacted]'];
      }
      return [key, value];
    })
  );
}

// Check if gtag is available
function isGtagAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

// Get consent status from localStorage
export function getConsentStatus(): 'granted' | 'denied' | 'pending' {
  if (typeof window === 'undefined') return 'pending';
  const consent = localStorage.getItem('soulsafe_analytics_consent');
  if (consent === 'granted') return 'granted';
  if (consent === 'denied') return 'denied';
  return 'pending';
}

// Set consent status
export function setConsentStatus(status: 'granted' | 'denied'): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('soulsafe_analytics_consent', status);
  
  if (isGtagAvailable()) {
    window.gtag('consent', 'update', {
      analytics_storage: status,
    });
  }
}

// Initialize GA4 with consent mode
export function initializeGA4(): void {
  if (typeof window === 'undefined') return;
  
  // Load gtag script dynamically if not already present
  if (!document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  // Set default consent (denied until user consents)
  const currentConsent = getConsentStatus();
  window.gtag('consent', 'default', {
    analytics_storage: currentConsent === 'granted' ? 'granted' : 'denied',
    ad_storage: 'denied', // We don't use ads
    wait_for_update: 500,
  });

  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false, // We'll manually track page views
  });
}

// Track page view
export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (!isGtagAvailable() || getConsentStatus() !== 'granted') return;
  
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
}

// Track custom event
export function trackEvent(
  eventName: string,
  parameters?: Record<string, unknown>
): void {
  if (!isGtagAvailable() || getConsentStatus() !== 'granted') return;
  
  const sanitizedParams = parameters ? sanitizeEventData(parameters) : {};
  
  window.gtag('event', eventName, sanitizedParams);
}

// Set user ID (anonymized)
export function setUserId(userId: string | null): void {
  if (!isGtagAvailable()) return;
  
  if (userId) {
    // Hash the user ID for anonymization
    const anonymizedId = btoa(userId).substring(0, 20);
    window.gtag('set', { user_id: anonymizedId });
  }
}

// Soul Safe specific event tracking
export const GA4Events = {
  // Survivor Engagement (Tier 1)
  accountCreated: () => trackEvent('account_created'),
  assessmentStarted: () => trackEvent('assessment_started'),
  assessmentCompleted: (stage: string) => trackEvent('assessment_completed', { recovery_stage: stage }),
  moduleStarted: (moduleId: string) => trackEvent('module_started', { module_id: moduleId }),
  moduleCompleted: (moduleId: string) => trackEvent('module_completed', { module_id: moduleId }),
  lessonViewed: (lessonId: string, moduleId?: string) => 
    trackEvent('lesson_viewed', { lesson_id: lessonId, module_id: moduleId }),
  reflectionSubmitted: () => trackEvent('reflection_submitted'),
  progressMilestone: (milestone: string) => trackEvent('progress_milestone', { milestone }),
  survivorCertificationCompleted: () => trackEvent('survivor_certification_completed'),
  
  // Leadership Events (Tier 2)
  leaderTierSelected: () => trackEvent('leader_tier_selected'),
  leaderTrainingStarted: () => trackEvent('leader_training_started'),
  governanceModuleViewed: () => trackEvent('governance_module_viewed'),
  safetyFrameworkAccessed: () => trackEvent('safety_framework_accessed'),
  quizStarted: (quizId: string) => trackEvent('quiz_started', { quiz_id: quizId }),
  quizCompleted: (quizId: string, score: number, passed: boolean) => 
    trackEvent('quiz_completed', { quiz_id: quizId, score, passed }),
  leaderCertificationCompleted: () => trackEvent('leader_certification_completed'),
  certificateDownloaded: (type: string) => trackEvent('certificate_downloaded', { certificate_type: type }),
  
  // Safety & Learning Flow Events
  moduleDropoff: (moduleId: string, step: number) => 
    trackEvent('module_dropoff', { module_id: moduleId, step }),
  sessionReturn: () => trackEvent('session_return'),
  
  // Monetization Events (no payment details)
  subscriptionStarted: (tier: string) => trackEvent('subscription_started', { tier }),
  subscriptionUpgraded: (fromTier: string, toTier: string) => 
    trackEvent('subscription_upgraded', { from_tier: fromTier, to_tier: toTier }),
  subscriptionCancelled: () => trackEvent('subscription_cancelled'),
  paymentFailure: () => trackEvent('payment_failure'),
  trialEnded: () => trackEvent('trial_ended'),
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}
